import { Avatar, Button, Text } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraCommentData } from '../../../../api/endpoints/jira';
import { Icon } from '../../../../concepts/resources/Icon';
import { Colors } from '../../../../ui';
import { MeldedInput } from '../../../../ui/MeltedInput';
import { FlexScroller } from '../../../../ui/Scrollers';
import { useResolve } from '../../../Container';
import { EmptyBlock } from '../../../dashboard/elements/EmptyBlock';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { AdfDoc } from '../../../external/atlassian/adf/AdfModelTypes';
import { AdfViewer } from '../../../external/atlassian/adf/AdfViewer';
import { JiraIssueSidebarActions } from './JiraIssueSidebarActions';

const useJiraIssueCommentsQuery = (jiraIssueId: string) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['jira.issues.comments.list', jiraIssueId],
        queryFn: () => api.exec(v2.jira.issues.comments.list(jiraIssueId)),
    });
};

const useJiraCreateCommentMutation = (jiraIssueId: string) => {
    const api = useResolve(ApiClient);
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ body }: { body: any }) => api.exec(v2.jira.issues.comments.create({ jiraIssueId, body })),
        onSettled() {
            qc.invalidateQueries({ queryKey: ['jira.issues.comments.list', jiraIssueId] });
        }
    });
};

const JiraComment = React.memo(({ jiraIssueId, comment }: { jiraIssueId: string, comment: JiraCommentData }) => {
    return <Flexer flex="row" gap="1" padding="0 0 0 .5">
        <Flexer>
            <Avatar src={comment.author.avatarUrl} size="md" />
        </Flexer>
        <Flexer flex="column" gap=".5">
            <Flexer flex="column" gap=".25">
                <Text c={Colors.text} fw={500} size="md">{comment.author.displayName}</Text>
                <Text c={Colors.textLighter} size="sm">{new Date(comment.created).toLocaleString()}</Text>
            </Flexer>
            <AdfViewer jiraIssueId={jiraIssueId} doc={comment.body} />
        </Flexer>
        <Flexer width="4" />
    </Flexer>;
});

const JiraCommentInput = React.memo(({ jiraIssueId }: { jiraIssueId: string }) => {

    const create = useJiraCreateCommentMutation(jiraIssueId);
    const [value, setValue] = useState('');

    const onSubmit = () => {
        if (!value.trim()) return;

        const body = AdfDoc.create([
            {
                type: 'paragraph',
                content: [
                    { type: 'text', text: value },
                ]
            },
        ]);

        create.mutate({ body });
        setValue('');
    };

    return <MeldedInput autoFocus value={value} placeholder="Add a comment..." onChange={setValue} onCommit={onSubmit} >
        <MeldedInput.Actions right={<Flexer flex="row" gap=".5" align="center">
            <Button variant="filled" size="xs" loading={create.isLoading} color={create.isLoading ? 'observe' : 'primary'} onClick={onSubmit}>
                <Icon size="1" src="deqo://icons/hero/arrow-up" />
            </Button>
        </Flexer>} />
    </MeldedInput>;
});

export const JiraIssueCommentsView = React.memo(({ jiraIssueId }: { jiraIssueId: string }) => {

    const comments = useJiraIssueCommentsQuery(jiraIssueId);
    const [force, setForce] = useState(false);

    return <Flexer grow flex="column">
        <Flexer grow flex="column" gap="2">
            {(comments.data?.length === 0 && !force) &&
                <Flexer flex="column" grow padding=".5">
                    <EmptyBlock grow minHeight="10" title="This issue has no comments yet...">
                        <Button variant="filled" onClick={() => setForce(true)}>
                            Add Comment
                        </Button>
                    </EmptyBlock>
                </Flexer>
            }
            {(comments.data?.length > 0 || force) &&
                <FlexScroller shadow>
                    <Flexer flex="column" gap="3" padding="1 .5 2 .5" minHeight="29">
                        {comments.data?.map(x => <JiraComment key={x.id} jiraIssueId={jiraIssueId} comment={x} />)}
                    </Flexer>
                    <Flexer padding=".5">
                        <JiraCommentInput jiraIssueId={jiraIssueId} />
                    </Flexer>
                </FlexScroller>
            }
        </Flexer>
        <JiraIssueSidebarActions jiraIssueId={jiraIssueId} />
    </Flexer>;
});
